import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { ExportMenu } from './ExportMenu';
import { ShoppingListCategory } from './ShoppingListCategory';
import { AddItemModal } from './AddItemModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { EditQuantityDrawer } from './EditQuantityDrawer';
import { DeleteListDrawer } from './DeleteListDrawer';
import { RegenerateListDrawer } from './RegenerateListDrawer';
import BottomNav from './BottomNav';
import { useNavigate } from 'react-router-dom';
import { formatWeekLabel, getWeekStartISO, addWeeks } from '../utils/dateUtils';
import { apiGet, apiPost, apiPatch, apiDelete } from '../utils/apiClient';
import { readViewWeekOffset, writeViewWeekOffset } from '../utils/viewWeek';

// Parse quantity input like "500g" or "1 1/2 cup" into number + unit
const parseQuantityInput = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return { qtyNumber: null, unit: '' };

  const match = s.match(/^(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)(.*)$/);
  if (!match) return { qtyNumber: null, unit: '' };

  const qtyPart = match[1].trim();
  const unitPart = match[2].trim();

  const tokens = qtyPart.split(/\s+/);
  let total = 0;
  for (const t of tokens) {
    if (/^\d+\/\d+$/.test(t)) {
      const [n, d] = t.split('/').map(Number);
      if (!d) return { qtyNumber: null, unit: unitPart };
      total += n / d;
    } else if (/^\d+(?:\.\d+)?$/.test(t)) {
      total += Number(t);
    }
  }
  const qtyNumber = isNaN(total) ? null : Math.round(total * 100) / 100;
  return { qtyNumber, unit: unitPart };
};

// Format numeric quantities: whole numbers stay as-is, others rounded to 2 decimals
const formatQuantityNumber = (q) => {
  if (typeof q !== 'number' || !isFinite(q)) return String(q);
  const roundedInt = Math.round(q);
  if (Math.abs(q - roundedInt) < 1e-9) return String(roundedInt);
  const rounded2 = Math.round(q * 100) / 100;
  return String(rounded2);
};

export function ShoppingList({ onTabChange, activeTab, weeksShoppingData, currentWeekIndex, actualCurrentWeekIndex, currentWeekStartISO, refreshKey, onPreviousWeek, onNextWeek}) {
  const CHECK_DELAY_MS = 2000;
  // Backend-first: start empty; widgets hydrate via fetchWeekList()
  const [weekData, setWeekData] = useState([]);
  const [isLoadingWeek, setIsLoadingWeek] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isEditQuantityDrawerOpen, setIsEditQuantityDrawerOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [isDeleteListDrawerOpen, setIsDeleteListDrawerOpen] = useState(false);
  const [isRegenerateListDrawerOpen, setIsRegenerateListDrawerOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  // 0=Sun..6=Sat. Seed from the cached preference so a freshly-mounted standalone
  // page uses the correct week start immediately (avoids a Monday-dates flash before
  // /user/preferences resolves); default Monday only when nothing is cached yet.
  const [weekStartDow, setWeekStartDow] = useState(() => {
    try {
      const raw = localStorage.getItem('mp:weekStartDow');
      if (raw === null) return 1;
      const n = Number(raw);
      return Number.isInteger(n) && n >= 0 && n <= 6 ? n : 1;
    } catch {
      return 1;
    }
  });
  // Standalone (routed) week navigation: weeks relative to today, clamped to 2 weeks
  // back to match MealPlan. Ignored when MealPlan supplies week props (internal-tab mode).
  // Seeded from the shared viewed-week offset so arriving from Meal Plan lands on the same week.
  const [weekOffset, setWeekOffset] = useState(() => readViewWeekOffset() ?? 0);
  const navigate = useNavigate();
  const CHECKED_STORAGE_KEY = 'shoppingListCheckedStates';
  const ORDER_STORAGE_KEY = 'shoppingListOrderByWeek';
  const checkCommitTimers = useRef({});
    // Use a safe index if not provided
    const safeIndex =
      Number.isInteger(currentWeekIndex) && currentWeekIndex >= 0 ? currentWeekIndex : 0;

  const buildStableKey = (item) => {
    const n = String(item.name || '').trim().toLowerCase();
    const u = String(item.unit || '').trim().toLowerCase();
    return `${n}__${u}`;
  };

  const loadCheckedMap = () => {
    try {
      const raw = localStorage.getItem(CHECKED_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const saveCheckedMap = (map) => {
    try {
      localStorage.setItem(CHECKED_STORAGE_KEY, JSON.stringify(map));
    } catch {}
  };

  const getChecked = (weekISO, stableKey) => {
    const map = loadCheckedMap();
    return !!(map[weekISO] && map[weekISO][stableKey]);
  };

  const setChecked = (weekISO, stableKey, value) => {
    const map = loadCheckedMap();
    if (!map[weekISO]) map[weekISO] = {};
    map[weekISO][stableKey] = !!value;
    saveCheckedMap(map);
  };

  const clearItemChecked = (weekISO, stableKey) => {
    const map = loadCheckedMap();
    if (map[weekISO]) {
      delete map[weekISO][stableKey];
      saveCheckedMap(map);
    }
  };

  // Clear any pending commit timers on unmount
  useEffect(() => {
    const timersRef = checkCommitTimers.current;
    return () => {
      Object.values(timersRef || {}).forEach((timerId) => clearTimeout(timerId));
    };
  }, []);

  // Are there any items still in the pending (post-check debounce) window this week?
  // Used to gate the fallback sweeper so it only runs when there's actually something to commit.
  const weekForPendingCheck = Array.isArray(weekData) ? weekData[safeIndex] : null;
  const hasPendingItems = !!weekForPendingCheck?.categories && Object.values(weekForPendingCheck.categories).some(
    (list) => Array.isArray(list) && list.some((it) => it && it.pendingUntil)
  );

  // Fallback sweeper: if a pendingUntil has expired (timer cancelled or missed), clear it and re-render.
  // Only runs while items are pending, so it isn't burning CPU 2.5x/sec when the list is idle.
  useEffect(() => {
    if (!hasPendingItems) return undefined;
    const sweep = () => {
      setWeekData((prev) => {
        const now = Date.now();
        const data = Array.isArray(prev) ? [...prev] : [];
        if (!data[safeIndex] || !data[safeIndex].categories) return prev;
        const week = data[safeIndex];
        const cats = { ...(week.categories || {}) };
        let updated = false;
        Object.keys(cats).forEach((catKey) => {
          const list = cats[catKey];
          if (!Array.isArray(list)) return;
          const nextList = list.map((it) => {
            if (it.pendingUntil && it.pendingUntil <= now && it.checked) {
              updated = true;
              return { ...it, pendingUntil: null, checked: true };
            }
            return it;
          });
          if (updated) {
            cats[catKey] = nextList;
          }
        });
        if (!updated) return prev;
        data[safeIndex] = { ...week, categories: cats };
        return data;
      });
    };
    const intervalId = setInterval(sweep, 400);
    return () => clearInterval(intervalId);
  }, [safeIndex, hasPendingItems]);

  const clearWeekChecked = (weekISO) => {
    const map = loadCheckedMap();
    if (map[weekISO]) {
      delete map[weekISO];
      saveCheckedMap(map);
    }
  };

  const loadOrderMap = () => {
    try {
      const raw = localStorage.getItem(ORDER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const saveOrderMap = (map) => {
    try {
      localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(map));
    } catch {}
  };

  // Category collapse state - persisted in localStorage
  const [categoryCollapseState, setCategoryCollapseState] = useState(() => {
    try {
      const saved = localStorage.getItem('shoppingListCategoryCollapseState');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Responsive screen detection
useEffect(() => {
  const mediaQuery = window.matchMedia('(min-width: 768px)');
  
  const handleMediaChange = (e) => {
    setIsLargeScreen(e.matches);
  };
  
  // Set initial value
  setIsLargeScreen(mediaQuery.matches);
  
  // Listen for changes
  mediaQuery.addEventListener('change', handleMediaChange);
  
  // Cleanup
  return () => mediaQuery.removeEventListener('change', handleMediaChange);
}, []);

  // Load weekStartDay preference so standalone ShoppingList uses the same week start as MealPlan
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prefs = await apiGet('/user/preferences');
        const raw = String(prefs?.weekStartDay || 'monday').toLowerCase();
        const map = {
          sunday: 0,
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
        };
        const dow = map[raw] ?? 1;
        if (!cancelled) {
          setWeekStartDow(dow);
          try { localStorage.setItem('mp:weekStartDow', String(dow)); } catch { /* ignore */ }
        }
      } catch (e) {
        console.warn('Failed to load weekStartDay preference in ShoppingList, defaulting to Monday:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  
  // Persist collapse state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('shoppingListCategoryCollapseState', JSON.stringify(categoryCollapseState));
    } catch (error) {
      console.error('Failed to save collapse state:', error);
    }
  }, [categoryCollapseState]);
  
  // Use provided week from Meal Plan, else derive from the internal offset (standalone route)
  const effectiveWeekStartISO =
    currentWeekStartISO || getWeekStartISO(addWeeks(new Date(), weekOffset), weekStartDow);

  // Compute label from the effective week start (same as Meal Plan)
  const weekStartForLabel = effectiveWeekStartISO
    ? new Date(`${effectiveWeekStartISO}T00:00:00Z`)
    : new Date();
  const computedWeekLabel = formatWeekLabel(weekStartForLabel);

  // Week navigation + "This Week" badge: use MealPlan's callbacks/indices when embedded
  // as a tab; fall back to the internal offset when rendered as a standalone route.
  const handlePrevWeek = onPreviousWeek || (() => setWeekOffset((o) => Math.max(-2, o - 1)));
  const handleNextWeek = onNextWeek || (() => setWeekOffset((o) => o + 1));
  const isThisWeek =
    Number.isInteger(currentWeekIndex) && Number.isInteger(actualCurrentWeekIndex)
      ? currentWeekIndex === actualCurrentWeekIndex
      : weekOffset === 0;

  // Persist the viewed week so Meal Plan opens on the same week (standalone route only).
  const isStandaloneWeekNav = typeof onPreviousWeek !== 'function';
  useEffect(() => {
    if (isStandaloneWeekNav) writeViewWeekOffset(weekOffset);
  }, [isStandaloneWeekNav, weekOffset]);
  // Guard against undefined data/index
  const currentWeek =
    Array.isArray(weekData) && weekData[safeIndex]
      ? weekData[safeIndex]
      : { label: computedWeekLabel, categories: {} };
  
  // Helper: load shopping list for current week from backend
  const fetchWeekList = async () => {
    if (!effectiveWeekStartISO) return;
    setIsLoadingWeek(true);
    try {
      const items = await apiGet(`shoppingList/week-start/${encodeURIComponent(effectiveWeekStartISO)}`);
       const categories = {};
       items.forEach(item => {
         const cat = item.category || 'Groceries';
         if (!categories[cat]) categories[cat] = [];
         categories[cat].push({
           id: item._id || `${cat}-${item.name}`,
           name: item.name,
           // Items with no number (e.g. "parsley, a handful") have an empty quantity, never 0 or NaN.
           quantity: typeof item.quantity === 'number' ? formatQuantityNumber(item.quantity) : '',
           unit: item.unit,
           stableKey: buildStableKey(item),
           checked: getChecked(effectiveWeekStartISO, buildStableKey(item)),
           pendingUntil: null,
         });
       });

      // Apply saved order per category (per week); fallback to alphabetical if no saved order
      const orderMap = loadOrderMap();
      const weekOrder = orderMap[effectiveWeekStartISO] || {};
      Object.keys(categories).forEach((catKey) => {
        const savedOrder = Array.isArray(weekOrder[catKey]) ? weekOrder[catKey] : null;
        if (savedOrder && savedOrder.length) {
          const byKey = new Map();
          categories[catKey].forEach((it) => byKey.set(it.stableKey, it));
          const ordered = savedOrder
            .map((k) => byKey.get(k))
            .filter(Boolean);
          // append any new items not in saved order at the end
          categories[catKey].forEach((it) => {
            if (!savedOrder.includes(it.stableKey)) ordered.push(it);
          });
          categories[catKey] = ordered;
        } else {
          categories[catKey].sort((a, b) => {
            const nameA = (a.name || '').toString();
            const nameB = (b.name || '').toString();
            return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
          });
        }
      });
      setWeekData(prev => {
        const newData = Array.isArray(prev) ? [...prev] : [];
        newData[safeIndex] = { label: computedWeekLabel, categories };
        return newData;
      });
    } catch (e) {
      console.warn('Failed to load shopping list for week', effectiveWeekStartISO, e);
    } finally {
      setIsLoadingWeek(false);
    }
   };
  
  // Load shopping list for embedded (tab) or standalone route
  useEffect(() => {
    if (!activeTab || activeTab === 'shopping-list') {
      fetchWeekList();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, effectiveWeekStartISO, refreshKey]);
  
  // Base category order with default categories first, then any custom categories
  const defaultCategories = ['Fruit & Veg', 'Dairy', 'Groceries', 'Meat'];
  const allCategoryNames = Object.keys(currentWeek.categories || {});
  const customCategories = allCategoryNames.filter(cat => !defaultCategories.includes(cat));
  const categoryOrder = [...defaultCategories, ...customCategories];

  // Apply a checked/pending update for one item to the current week's in-memory data.
  const applyItemState = (itemId, { checked, pendingUntil }) => {
    setWeekData((prev) => {
      const data = Array.isArray(prev) ? [...prev] : [];
      if (!data[safeIndex] || !data[safeIndex].categories) return prev;
      const week = data[safeIndex];
      const cats = { ...(week.categories || {}) };
      let updated = false;
      Object.keys(cats).forEach((catKey) => {
        const list = cats[catKey];
        if (!Array.isArray(list)) return;
        const idx = list.findIndex((it) => it.id === itemId);
        if (idx === -1) return;
        const next = [...list];
        next[idx] = { ...next[idx], checked, pendingUntil };
        cats[catKey] = next;
        updated = true;
      });
      if (!updated) return prev;
      data[safeIndex] = { ...week, categories: cats };
      return data;
    });
  };

  const handleToggleItem = (itemId) => {
    // Resolve the item + its next state SYNCHRONOUSLY from current state. Do NOT read these
    // from inside a setWeekData updater — React doesn't guarantee the updater runs before the
    // code after setState, so the persist step was being skipped intermittently (key=null),
    // toggling items visually without ever writing to localStorage.
    const week = Array.isArray(weekData) ? weekData[safeIndex] : null;
    let target = null;
    if (week?.categories) {
      for (const catKey of Object.keys(week.categories)) {
        const found = (week.categories[catKey] || []).find((it) => it.id === itemId);
        if (found) { target = found; break; }
      }
    }
    if (!target) return;

    const key = target.stableKey || buildStableKey(target);
    const nextChecked = !target.checked;

    // Persist immediately (both directions) so the change survives navigating away.
    if (nextChecked) setChecked(effectiveWeekStartISO, key, true);
    else clearItemChecked(effectiveWeekStartISO, key);

    // Cancel any in-flight visual-commit timer for this item.
    if (checkCommitTimers.current[key]) {
      clearTimeout(checkCommitTimers.current[key]);
      delete checkCommitTimers.current[key];
    }

    // Update the visual state: checked now; keep it "in place" via pendingUntil when checking.
    applyItemState(itemId, {
      checked: nextChecked,
      pendingUntil: nextChecked ? Date.now() + CHECK_DELAY_MS : null,
    });

    // On check, clear pendingUntil after the window so the item can re-sort to the bottom.
    if (nextChecked) {
      checkCommitTimers.current[key] = setTimeout(() => {
        applyItemState(itemId, { checked: true, pendingUntil: null });
        delete checkCommitTimers.current[key];
      }, CHECK_DELAY_MS);
    }
  };

  const handleDeleteClick = (itemId) => {
    // Find the item in categories
    const categories = currentWeek.categories || {};
    let foundItem = null;
    
    Object.keys(categories).forEach(categoryKey => {
      const item = categories[categoryKey].find(item => item.id === itemId);
      if (item) {
        foundItem = item;
      }
    });
    
    if (foundItem) {
      setItemToDelete(foundItem);
      setIsDeleteModalOpen(true);
    }
  };

  const handleEditClick = (itemId) => {
    // Find the item in categories
    const categories = currentWeek.categories || {};
    let foundItem = null;
    let foundCategory = null;
    
    Object.keys(categories).forEach(categoryKey => {
      const item = categories[categoryKey].find(item => item.id === itemId);
      if (item) {
        foundItem = item;
        foundCategory = categoryKey;
      }
    });
    
    if (foundItem && foundCategory) {
      setItemToEdit({ ...foundItem, category: foundCategory });
      setIsEditQuantityDrawerOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await apiDelete(`shoppingList/${encodeURIComponent(itemToDelete.id)}`);
      if (effectiveWeekStartISO) clearItemChecked(effectiveWeekStartISO, itemToDelete.stableKey || buildStableKey(itemToDelete));
      await fetchWeekList();
    } catch (e) {
      console.warn('Failed to delete shopping list item', e);
    } finally {
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleExportCSV = () => {
    // Build CSV content
    let csv = 'Category,Item,Quantity\n';
    
    categoryOrder.forEach(category => {
      const items = currentWeek.categories[category] || [];
      items.forEach(item => {
        const unit = typeof item.unit === 'string' ? item.unit.trim() : '';
        const qty = item.quantity === '' || item.quantity == null ? '' : String(item.quantity);
        const qtyWithUnit = qty && unit && unit.toLowerCase() !== 'unit' ? `${qty} ${unit}` : qty;
        csv += `"${category}","${item.name}","${qtyWithUnit}"\n`;
      });
    });

    // Create download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shopping-list-${currentWeek.label.replace(/\s+/g, '-').toLowerCase()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExportText = () => {
    // Build text content
    let text = `Shopping List - ${currentWeek.label || 'This Week'}\n\n`;
    
    categoryOrder.forEach(category => {
      const items = currentWeek.categories[category] || [];
      if (items.length > 0) {
        text += `${category}\n`;
        text += '─'.repeat(30) + '\n';
        items.forEach(item => {
          const unit = typeof item.unit === 'string' ? item.unit.trim() : '';
          const qty = item.quantity === '' || item.quantity == null ? '' : String(item.quantity);
          const qtyWithUnit = qty && unit && unit.toLowerCase() !== 'unit' ? `${qty} ${unit}` : qty;
          // No quantity: the description already says it all ("parsley, a handful").
          text += qtyWithUnit ? `${item.name} - ${qtyWithUnit}\n` : `${item.name}\n`;
        });
        text += '\n';
      }
    });

    // Create download
    const blob = new Blob([text], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shopping-list-${(currentWeek.label || 'this-week').replace(/\\s+/g, '-').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleAddItem = async (name, quantity, category) => {
    if (!effectiveWeekStartISO) return;
    const { qtyNumber, unit } = parseQuantityInput(quantity);
    try {
      await apiPost('shoppingList', {
        name,
        quantity: typeof qtyNumber === 'number' ? qtyNumber : quantity,
        unit,
        category,
        weekStartDate: effectiveWeekStartISO,
      });
      await fetchWeekList();
    } catch (e) {
      console.warn('Failed to add shopping list item', e);
    }
  };

  // Check if week has any items
  const hasItems = categoryOrder.some(cat => 
    currentWeek.categories[cat] && currentWeek.categories[cat].length > 0
  );

  // Handle moving items up or down within a category
  const handleMoveItem = (itemId, direction) => {
    setWeekData(prevData => {
      const newData = [...prevData];
      const categories = newData[safeIndex].categories;
      
      // Find the item, its category, and index
      let foundCategory = null;
      let itemIndex = -1;
      
      for (const categoryKey of Object.keys(categories)) {
        const idx = categories[categoryKey].findIndex(item => item.id === itemId);
        if (idx !== -1) {
          foundCategory = categoryKey;
          itemIndex = idx;
          break;
        }
      }
      
      if (!foundCategory) return prevData;
      
      const categoryIndex = categoryOrder.indexOf(foundCategory);
      const items = [...categories[foundCategory]];
      
      if (direction === 'up') {
        if (itemIndex > 0) {
          // Move within same category
          [items[itemIndex], items[itemIndex - 1]] = [items[itemIndex - 1], items[itemIndex]];
          categories[foundCategory] = items;
        } else if (categoryIndex > 0) {
          // Move to previous category (end of list)
          const prevCategory = categoryOrder[categoryIndex - 1];
          if (categories[prevCategory] && categories[prevCategory].length > 0) {
            const [itemToMove] = items.splice(itemIndex, 1);
            categories[foundCategory] = items;
            categories[prevCategory] = [...categories[prevCategory], itemToMove];
          }
        }
      } else { // direction === 'down'
        if (itemIndex < items.length - 1) {
          // Move within same category
          [items[itemIndex], items[itemIndex + 1]] = [items[itemIndex + 1], items[itemIndex]];
          categories[foundCategory] = items;
        } else if (categoryIndex < categoryOrder.length - 1) {
          // Move to next category (beginning of list)
          const nextCategory = categoryOrder[categoryIndex + 1];
          if (categories[nextCategory]) {
            const [itemToMove] = items.splice(itemIndex, 1);
            categories[foundCategory] = items;
            categories[nextCategory] = [itemToMove, ...categories[nextCategory]];
          }
        }
      }
      
      return newData;
    });
  };

  // Handle reordering items via drag and drop
  const handleReorder = (categoryName, newOrder) => {
    // Handle within-category reordering
    setWeekData(prevData => {
      const newData = [...prevData];
      newData[safeIndex].categories[categoryName] = newOrder;
      return newData;
    });

    // Persist order for this week/category using stable keys
    const orderMap = loadOrderMap();
    if (!orderMap[effectiveWeekStartISO]) orderMap[effectiveWeekStartISO] = {};
    orderMap[effectiveWeekStartISO][categoryName] = newOrder.map((it) => it.stableKey || buildStableKey(it));
    saveOrderMap(orderMap);
  };

  const handleMoveItemToCategory = (itemId, fromCategory, toCategory, targetIndex) => {
    // Handle cross-category movement
    setWeekData(prevData => {
      const newData = [...prevData];
      const categories = newData[safeIndex].categories;
      
      // Find and remove item from source category
      const itemToMove = categories[fromCategory].find(item => item.id === itemId);
      if (!itemToMove) return prevData;
      
      categories[fromCategory] = categories[fromCategory].filter(item => item.id !== itemId);
      
      // Add item to destination category at specified index
      if (!categories[toCategory]) {
        categories[toCategory] = [];
      }
      
      if (targetIndex !== undefined) {
        // Insert at specific position
        const newCategoryItems = [...categories[toCategory]];
        newCategoryItems.splice(targetIndex, 0, itemToMove);
        categories[toCategory] = newCategoryItems;
      } else {
        // Add to end if no index specified
        categories[toCategory] = [...categories[toCategory], itemToMove];
      }
      
      return newData;
    });
    
    // In production: Call backend API to update item category
    // fetch(`/api/shopping-list/items/${itemId}/move`, {
    //   method: 'PUT',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ fromCategory, toCategory, targetIndex })
    // });
  };

  const handleTabChange = (tab) => {
    if (typeof onTabChange === 'function') return onTabChange(tab);
    switch (tab) {
      case 'meal-plan':
        navigate('/MealPlan');
        break;
      case 'shopping-list':
        navigate('/ShoppingList');
        break;
      case 'recipes':
        navigate('/AllRecipes');
        break;
      case 'more':
        navigate('/More');
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-background flex justify-center">
      <div 
        className="w-full bg-background min-h-screen relative pb-24"
        style={{ 
          maxWidth: isLargeScreen ? '1200px' : '448px',
          transition: 'max-width 0.3s ease'
        }}
      >
        {/* Status Bar Space */}
        <div className="h-8" />

        {/* Header - Sticky during reorder mode */}
<header 
  className={`py-3 bg-background ${
    isReorderMode ? 'sticky top-0 z-20 shadow-sm' : ''
  }`}
  style={{ 
    paddingLeft: isLargeScreen ? '3rem' : '1.5rem',
    paddingRight: isLargeScreen ? '3rem' : '1.5rem'
  }}
>
  <div className="flex items-center justify-between">
    <div className="w-10" /> {/* Spacer for balance */}
    <h1 
      style={{ 
        fontSize: isLargeScreen ? '40px' : '28px',
        transition: 'font-size 0.3s ease'
      }}
    >
      Shopping List
    </h1>
    {isReorderMode ? (
      <button
        onClick={() => setIsReorderMode(false)}
        className="px-6 py-2.5 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover transition-colors shadow-md font-medium"
        aria-label="Done reordering"
      >
        Done
      </button>
    ) : (
      <ExportMenu 
        onExportCSV={handleExportCSV}
        onExportText={handleExportText}
        onReorder={hasItems ? () => setIsReorderMode(true) : undefined}
        onDeleteList={hasItems ? () => setIsDeleteListDrawerOpen(true) : undefined}
        onRegenerateList={() => setIsRegenerateListDrawerOpen(true)}
        isReorderMode={isReorderMode}
        hasItems={hasItems}
      />
    )}
  </div>
</header>

        {/* Main Content */}
        <main className="pb-8"
        style={{ 
            paddingLeft: isLargeScreen ? '3rem' : '1.5rem',
            paddingRight: isLargeScreen ? '3rem' : '1.5rem'
          }}
        
        >
          {/* Week Navigation */}
          <section className="mb-6">
            <div className="flex items-center justify-between mb-6">
              <button
                className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
                onClick={handlePrevWeek}
              >
                <ChevronLeft className="w-5 h-5 text-gray-700" />
              </button>

              <div className="flex flex-col items-center gap-1">
                <h3 className="text-base font-medium">{currentWeek.label}</h3>
                {isThisWeek && (
                  <span className="px-3 py-0.5 rounded-full text-xs badge-this-week">
                    This Week
                  </span>
                )}
              </div>

              <button
                className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
                onClick={handleNextWeek}
              >
                <ChevronRight className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </section>

          {/* Shopping List Categories */}
          {isLoadingWeek ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground mb-2">Please wait, shopping list loading...</p>
            </div>
          ) : hasItems ? (
            <section>
              {categoryOrder.map((category, index) => {
                // Calculate custom category index for color rotation
                const customCategoryIndex = customCategories.indexOf(category);
                // Get collapse state key - using just category name for simplicity
                const isExpanded = categoryCollapseState[category] !== false; // Default to expanded
                
                return (
                  <ShoppingListCategory
                    key={category}
                    title={category}
                    items={currentWeek.categories[category] || []}
                    onToggleItem={handleToggleItem}
                    onDeleteItem={handleDeleteClick}
                    onEditItem={handleEditClick}
                    onMoveItem={handleMoveItem}
                    onReorderItems={handleReorder}
                    onMoveItemToCategory={handleMoveItemToCategory}
                    isReorderMode={isReorderMode}
                    customCategoryIndex={customCategoryIndex >= 0 ? customCategoryIndex : undefined}
                    isExpanded={isExpanded}
                    onToggleExpand={() => {
                      setCategoryCollapseState(prevState => ({
                        ...prevState,
                        [category]: !isExpanded
                      }));
                    }}
                  />
                );
              })}
              
              {/* Removed Add Item Button from here - now using FAB */}
            </section>
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground mb-2">No items in shopping list</p>
              <p className="text-muted-foreground text-sm mb-6">
                Add meals to your plan to generate a shopping list
              </p>
              
              {/* Add Item Button for empty state */}
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="py-3 px-6 bg-primary text-primary-foreground rounded-full shadow-md hover:bg-primary-hover transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                <span>Add Item</span>
              </button>
            </div>
          )}
        </main>

        {/* Add Item Modal */}
        <AddItemModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddItem}
          categories={categoryOrder}
        />

        {/* Delete Confirm Modal */}
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          itemName={itemToDelete?.name || ''}
          onConfirm={handleDeleteConfirm}
          onCancel={() => {
            setIsDeleteModalOpen(false);
            setItemToDelete(null);
          }}
        />

        {/* Edit Quantity Drawer */}
        <EditQuantityDrawer
          isOpen={isEditQuantityDrawerOpen}
          itemName={itemToEdit?.name || ''}
          currentQuantity={itemToEdit?.quantity || ''}
          currentCategory={itemToEdit?.category || categoryOrder[0]}
          categories={Object.keys(currentWeek.categories)}
          onSave={async (newName, newQuantity, newCategory) => {
             if (!itemToEdit) return;
             try {
               const { qtyNumber, unit } = parseQuantityInput(newQuantity);
               const body = {
                 quantity: typeof qtyNumber === 'number' ? qtyNumber : newQuantity,
                 unit,
                 name: newName,
               };
               if (newCategory && newCategory !== itemToEdit.category) body.category = newCategory;

         await apiPatch(`shoppingList/${encodeURIComponent(itemToEdit.id)}`, body);
               await fetchWeekList();
             } catch (e) {
               console.warn('Failed to update shopping list item', e);
             } finally {
               setIsEditQuantityDrawerOpen(false);
               setItemToEdit(null);
             }
           }}
          onDelete={async () => {
             if (!itemToEdit) return;
             try {
           await apiDelete(`shoppingList/${encodeURIComponent(itemToEdit.id)}`);
               await fetchWeekList();
             } catch (e) {
               console.warn('Failed to delete shopping list item', e);
             } finally {
               setIsEditQuantityDrawerOpen(false);
               setItemToEdit(null);
             }
           }}
          onClose={() => {
            setIsEditQuantityDrawerOpen(false);
            setItemToEdit(null);
          }}
        />

        {/* Delete List Drawer */}
        <DeleteListDrawer
          isOpen={isDeleteListDrawerOpen}
          weekLabel={currentWeek.label}
          onConfirm={async () => {
             try {
         if (effectiveWeekStartISO) {
             await apiDelete(`shoppingList/week-start/${encodeURIComponent(effectiveWeekStartISO)}`);           }
               if (effectiveWeekStartISO) clearWeekChecked(effectiveWeekStartISO);
               await fetchWeekList();
             } catch (e) {
               console.warn('Failed to delete week shopping list', e);
             } finally {
               setIsDeleteListDrawerOpen(false);
             }
           }}
          onCancel={() => setIsDeleteListDrawerOpen(false)}
        />

        {/* Regenerate List Drawer */}
        <RegenerateListDrawer
          isOpen={isRegenerateListDrawerOpen}
          weekLabel={currentWeek.label}
          onConfirm={async () => {
            try {
              if (effectiveWeekStartISO) {
                await apiPost(`generate-shopping-list/week-start/${encodeURIComponent(effectiveWeekStartISO)}`, {});
              }
              await fetchWeekList();
            } catch (e) {
              console.warn('Failed to regenerate shopping list', e);
            } finally {
              setIsRegenerateListDrawerOpen(false);
            }
          }}
          onCancel={() => setIsRegenerateListDrawerOpen(false)}
        />

        {/* Floating Add Item Button */}
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="fixed bottom-20 right-6 md:bottom-24 md:right-12 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary-hover transition-colors flex items-center justify-center"
          aria-label="Add Item"
        >
          <Plus className="w-6 h-6" />
        </button>

        {/* Bottom Navigation - always visible */}
        <BottomNav 
          activeTab="shopping-list"
          onTabChange={handleTabChange}
        />
      </div>
    </div>
  );
}
export default ShoppingList;